/**
 * A lease's view of a held row (`SheetHandle.rows` hands each lease its own):
 * the policy that says how each member of the row answers once the lease
 * that handed it out is released (`LEASED_MEMBERS`), and the proxies that
 * apply it — to the row (`leaseView`) and to the narrowed cell facades it
 * hands out (`leaseFace`).
 *
 * **SDK-internal**: `leasedRowOf` (`sheet-row.ts`) builds the view of a
 * `SheetRow.Handle`, handing in the lease's check, the row's own face
 * builders and how a durable verb reports a refusal (`LeaseView.Lease`). It
 * names no row type, so it sits outside the core's type knot.
 *
 * @internal SDK-private; `lib.ts` does not export it.
 */

import type { Option } from "@tento-lona/sheets";

/**
 * The key a lease's view of a held row (`leaseView`) answers with the
 * shared row it forwards to, so the SDK's doors onto a row's state reach it
 * through either (`leaseViewTarget`). Unexported: nothing outside this module
 * can read it.
 */
const LEASE_TARGET: unique symbol = Symbol("SheetRow.Handle.leaseTarget");

/**
 * How a lease's view of a held row answers each member once its lease is
 * released (`leasedRowOf`): `read` forwards (a read of the row's own state
 * or cache, allowed as `handle.cells`'s cached reads are); `reject` — an
 * async verb — returns a rejected promise; `settled` — a durable verb —
 * reports through `settled` (`applied: on-settle`); `throw` — a synchronous
 * verb that reads through the network (`cell`, `cells`, `events`) — throws.
 * A method missing here is refused (`throw`): a verb added later is
 * lease-checked until it is listed.
 */
const LEASED_MEMBERS: ReadonlyMap<string, "read" | "reject" | "settled" | "throw" | "face"> = new Map([
  ["is", "read"],
  ["attributesAs", "read"],
  ["cell", "throw"],
  ["get", "reject"],
  ["peek", "read"],
  ["cells", "throw"],
  ["capabilities", "read"],
  ["mergeAttributes", "reject"],
  ["setLookupKey", "reject"],
  ["events", "throw"],
  ["write", "reject"],
  ["clear", "reject"],
  ["moveCell", "reject"],
  ["replaceCell", "reject"],
  ["update", "reject"],
  ["generateCell", "reject"],
  // The row's tree position and rows-side reads: read, as its own state is.
  ["attributes", "read"],
  ["as", "read"],
  ["children", "read"],
  ["childrenIds", "read"],
  ["parent", "read"],
  ["findAncestor", "read"],
  ["rootDataRow", "read"],
  ["findRowWithFacade", "read"],
  ["findFacade", "read"],
  ["findLeafRowsWithFacade", "read"],
  ["layoutSpec", "read"],
  ["initialScrollTop", "read"],
  ["requiresHeaderSecondRow", "read"],
  ["headerSecondRowKind", "read"],
  ["buildLabel", "read"],
  ["buildAriaLabel", "read"],
  // An alias row's prop write (`alias()`): its verb is lease-checked.
  ["alias", "face"],
  ["setProp", "reject"],
  ["writeDurable", "settled"],
  ["clearDurable", "settled"],
  ["updateDurable", "settled"],
  // The narrowed facades: handed out as they are, their verbs lease-checked
  // like the row's own (`leaseFace`).
  ["single", "face"],
  ["writableSingle", "face"],
  ["writableMulti", "face"],
]);

/** The shared row a lease's view forwards to, or `null` when `row` is not a lease's view. */
export function leaseViewTarget(row: object): Option<object> {
  const target: unknown = Reflect.get(row, LEASE_TARGET);
  return typeof target === "object" && target != null ? target : null;
}

/**
 * `lease`'s view of the shared held row `target`: it IS the row — the same
 * state and identity, `instanceof` its class — except that every verb that
 * can reach the network first asks `lease.held` (which throws once the lease
 * was released), and the members `lease.own` names answer with the lease's
 * own builders. Nothing is sent then: an async verb rejects, a durable verb
 * reports through `settled` (`lease.refusal`), a synchronous read of
 * the network throws. Reads of the row's own state stay allowed
 * (`LEASED_MEMBERS`).
 */
export function leaseView<R extends object>(target: R, lease: LeaseView.Lease): R {
  return new Proxy(target, {
    get(shared, key) {
      if (key === LEASE_TARGET) return shared;
      if (typeof key === "string" && Object.hasOwn(lease.own, key)) return lease.own[key];
      const value: unknown = Reflect.get(shared, key, shared);
      if (typeof value !== "function" || key === "constructor") return value;
      // Runs against the view, never the shared row (`valueOf` would answer it).
      if (inheritedFromObject(key, value)) return value;
      const mode = typeof key !== "string" || key in Object.prototype
        ? "read"
        : LEASED_MEMBERS.get(key) ?? "throw";
      const verb = `row.${String(key)}`;
      if (mode === "face") {
        return (...args: unknown[]): unknown => {
          const face: unknown = Reflect.apply(value, shared, args);
          return face == null || typeof face !== "object" ? face : leaseFace(face, lease.held, lease.refusal());
        };
      }
      return (...args: unknown[]): unknown => {
        if (mode !== "read") {
          try {
            lease.held(verb);
          } catch (err) {
            if (mode === "reject") return Promise.reject(err);
            if (mode === "settled") return lease.refusal()(err);
            throw err;
          }
        }
        return Reflect.apply(value, shared, args);
      };
    },
    set(shared, key, value) {
      return Reflect.set(shared, key, value, shared);
    },
  });
}

/**
 * A lease's view of a narrowed cell facade (`single()`, `writableSingle()`, `writableMulti()`): every
 * verb asks `held` first and refuses as the row's own verb of that name does
 * (`LEASED_MEMBERS`) — except a read of the row's own cache (`read`: `peek`),
 * which answers after the lease's release as `handle.cells`' cached reads
 * do; `cardinality` reads.
 */
export function leaseFace<F extends object>(
  face: F,
  held: (verb: string) => void,
  refusedDurable: (err: unknown) => unknown,
): F {
  return new Proxy(face, {
    get(target, key) {
      const value: unknown = Reflect.get(target, key, target);
      if (typeof value !== "function" || typeof key !== "string") return value;
      // Runs against the view, never the shared facade (`valueOf` would answer it).
      if (inheritedFromObject(key, value)) return value;
      const mode = LEASED_MEMBERS.get(key) ?? "throw";
      if (mode === "read") return (...args: unknown[]): unknown => Reflect.apply(value, target, args);
      const verb = `row.${key}`;
      return (...args: unknown[]): unknown => {
        try {
          held(verb);
        } catch (err) {
          if (mode === "reject") return Promise.reject(err);
          if (mode === "settled") return refusedDurable(err);
          throw err;
        }
        return Reflect.apply(value, target, args);
      };
    },
  });
}

/**
 * Whether `value`, read as `key` off a lease's target, is the
 * `Object.prototype` member itself (`valueOf`, `toString`, …) — not a
 * member the target overrides. A lease view answers it unbound, so it runs
 * against the view: bound to the target, `valueOf` would hand the unleased
 * object out.
 */
function inheritedFromObject(key: PropertyKey, value: unknown): boolean {
  return key in Object.prototype && Reflect.get(Object.prototype, key) === value;
}

/** Types that go with `leaseView`: `Lease`. */
export namespace LeaseView {
  /** One lease's side of its view of a held row. */
  export interface Lease {
    /** The lease's check: throws its `DisposedError` (naming `verb`) once the lease was released. */
    held(verb: string): void;
    /** The members the lease builds itself (`single`, `writableSingle`, `cells`): answered as they are. */
    readonly own: Readonly<Record<string, unknown>>;
    /**
     * How a durable verb reports a refusal through its `settled` (`applied:
     * on-settle`), watched under the row's durable owner as it is now.
     */
    refusal(): (err: unknown) => unknown;
  }
}
